import {
	AuthActionTypes,
	IAuthSetAuthenticatorsAction,
	IAuthSetMfaOOBChannelAction,
	IAuthSetSkipMfa,
	IMfaAuthenticator,
	OobChannelType,
} from '../../../types/auth'
import auth0Client from '../../../api/auth0'
import { AppThunkAction, history } from '../../index'
import {
	PAGE_LOGIN,
	PAGE_LOGIN_2FA_METHOD,
} from '../../../constants/navigation'
import { FIRST_LOGIN_CHANGE_PASSWORD } from '../../../constants/errorCodes'
import api from '../../../api'

const getMfaAuthenticatorsArray = async (
	mfaToken: string
): Promise<IMfaAuthenticator[]> => {
	try {
		const res = await api.auth.getMfaAuthenticators(mfaToken)

		return res.data
	} catch (e: any) {
		const error = e.message || 'Get mfa authenticators error'

		throw new Error(error)
	}
}

export const login = (
	userName: string,
	password: string
): AppThunkAction<Promise<void>> => {
	return async dispatch => {
		try {
			const result = await auth0Client.login(userName, password)
			const { accessToken } = result

			if (accessToken) {
				dispatch({
					type: AuthActionTypes.AUTH_LOGIN,
					payload: accessToken,
				})

				localStorage.setItem('access_token', accessToken)
			}
		} catch (e: any) {
			const mfaToken = e?.original?.response?.body?.mfa_token || ''
			const firstLoginChangePwdMsg =
				e?.original?.response?.body?.error_description?.message || ''

			if (firstLoginChangePwdMsg === FIRST_LOGIN_CHANGE_PASSWORD) {
				const changePwdRedirectUrl =
					e?.original?.response?.body?.error_description?.url || ''
				window.open(changePwdRedirectUrl, '_blank') ||
					window.location.replace(changePwdRedirectUrl)
				return
			}

			if (!mfaToken) {
				const error = e.description || e.code || 'Login error'
				throw new Error(error)
			}

			if (mfaToken) {
				const errorPayload = {
					mfaRequiredError: e.code,
					mfaRequiredErrorDescription: e.description,
				}
				const authenticators = await getMfaAuthenticatorsArray(mfaToken)

				dispatch({
					type: AuthActionTypes.AUTH_SET_MFA_TOKEN,
					payload: mfaToken,
				})

				if (authenticators && authenticators.length) {
					dispatch({
						type: AuthActionTypes.AUTH_SET_AUTHENTICATORS,
						payload: authenticators,
					})
				}

				dispatch({
					type: AuthActionTypes.AUTH_MFA_REQUIRED_ERROR,
					payload: errorPayload,
				})

				history.push(PAGE_LOGIN_2FA_METHOD)
			}
		}
	}
}

export const setMfaAuthenticators = (
	authenticators: Array<IMfaAuthenticator>
): IAuthSetAuthenticatorsAction => ({
	type: AuthActionTypes.AUTH_SET_AUTHENTICATORS,
	payload: authenticators,
})

export const setMfaOOBChannel = (
	oodChannel: string
): IAuthSetMfaOOBChannelAction => ({
	type: AuthActionTypes.AUTH_SET_OOB_CHANNEL,
	payload: oodChannel,
})

export const mfaChallenge = (
	authenticatorId: string,
	mfaToken: string
): AppThunkAction<Promise<void>> => {
	return async dispatch => {
		try {
			const res = await api.auth.mfaChallengeReq(
				authenticatorId,
				mfaToken
			)
			if (res.data?.oob_code) {
				dispatch({
					type: AuthActionTypes.AUTH_SET_OOB_CODE,
					payload: res.data.oob_code,
				})
			}
		} catch (e: any) {
			const error =
				e?.response?.data?.error_description ||
				e.message ||
				'auth0 challenge error'

			throw new Error(error)
		}
	}
}

export const regAuthenticator = (
	oobChannels: Array<OobChannelType>,
	phoneNumber: string,
	mfaToken: string
): AppThunkAction<Promise<void>> => {
	return async dispatch => {
		try {
			const res = await api.auth.registerAuthenticator(
				oobChannels,
				phoneNumber,
				mfaToken
			)
			if (res.data?.oob_code) {
				dispatch({
					type: AuthActionTypes.AUTH_SET_OOB_CODE,
					payload: res.data.oob_code,
				})
			}
		} catch (e: any) {
			const error =
				e?.response?.data?.error_description ||
				e.message ||
				'Register authenticator error'

			throw new Error(error)
		}
	}
}

export const completeLogin = (
	oobCode: string,
	bindingCode: string,
	mfaToken: string
): AppThunkAction<Promise<void>> => {
	return async dispatch => {
		try {
			const res = await api.auth.completeAuthentication(
				oobCode,
				bindingCode,
				mfaToken
			)
			const accessToken = res.data?.access_token || ''
			if (accessToken) {
				localStorage.setItem('access_token', accessToken)

				dispatch({
					type: AuthActionTypes.AUTH_LOGIN,
					payload: accessToken,
				})
			}
		} catch (e: any) {
			const error = e?.response?.data?.error_description || 'auth error'
			throw new Error(error)
		}
	}
}

export const logout = (): AppThunkAction => {
	return () => {
		auth0Client.logout({
			returnTo: `${process.env.REACT_APP_FRONT_URL}${PAGE_LOGIN}`,
			clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
		})

		localStorage.removeItem('access_token')
	}
}

export const passwordChange = (
	email: string
): AppThunkAction<Promise<void>> => {
	return async () => {
		try {
			await api.auth.changePassword(email)
		} catch (e: any) {
			const error = e.message || 'Change password error'

			throw new Error(error)
		}
	}
}

export const setSkipMfaAuth = (value: boolean): IAuthSetSkipMfa => ({
	type: AuthActionTypes.AUTH_SET_SKIP_MFA_AUTH,
	payload: value,
})

export const acceptTerms = (): AppThunkAction<Promise<void>> => {
	return async () => {
		try {
			await api.auth.acceptTermsOfService()
		} catch (e: any) {
			const error = e.message || 'Accept terms error'

			throw new Error(error)
		}
	}
}
